import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'

import { endpoints } from '../api/endpoints'
import { useSession } from '../auth/context'
import { LegalLinks } from '../pages/LegalPages'
import { SubscriptionBanner } from '../pages/SubscriptionPage'
import { t } from '../strings'
import { Icon, type IconName } from './Icon'

type NavItem = { to: string; label: string; icon: IconName; end?: boolean }

const groups: { title: string; items: NavItem[] }[] = [
  {
    title: t.groupSummary,
    items: [
      { to: '/', label: t.navHome, icon: 'chart', end: true },
      { to: '/transaksi', label: t.navSales, icon: 'receipt' },
    ],
  },
  {
    title: t.groupManage,
    items: [
      { to: '/data', label: t.navData, icon: 'grid' },
      { to: '/hp-kasir', label: t.navDevices, icon: 'phone' },
    ],
  },
  {
    title: t.groupAccount,
    items: [
      { to: '/pengaturan', label: t.navSettings, icon: 'settings' },
      { to: '/langganan', label: t.navSubscription, icon: 'card' },
    ],
  },
]

/** The five places an owner goes every day: always one tap away at the bottom of a phone. */
const bottomItems: NavItem[] = groups.flatMap((g) => g.items).filter((i) => i.to !== '/langganan')

function Links({ items }: { items: NavItem[] }) {
  return items.map((i) => (
    <NavLink key={i.to} to={i.to} end={i.end} className="nav-link">
      <Icon name={i.icon} />
      <span>{i.label}</span>
    </NavLink>
  ))
}

/** Account menu of the phone's top bar: subscription and sign out, a tap away, closed by a tap outside or Esc. */
function AccountMenu({ name }: { name: string }) {
  const { logout } = useSession()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  return (
    <div className="account" ref={box}>
      <button
        type="button"
        className="avatar"
        aria-label={t.accountMenu}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {(name.trim()[0] ?? 'G').toUpperCase()}
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu-name">{name}</div>
          <Link role="menuitem" to="/langganan">
            <Icon name="card" size={20} />
            {t.navSubscription}
          </Link>
          <button type="button" role="menuitem" onClick={() => void logout()}>
            <Icon name="logout" size={20} />
            {t.logout}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * The frame of every signed-in page. A wide screen gets a left sidebar with the whole menu (like a
 * back-office tool); a phone or tablet gets a slim top bar with the account menu and a bottom bar with
 * the five daily places. The same menu data feeds both, so they never disagree.
 */
export function Layout() {
  const { logout } = useSession()
  const { pathname } = useLocation()
  const biz = useQuery({ queryKey: ['business'], queryFn: endpoints.business })
  const name = biz.data?.name ?? ''
  return (
    <div className="shell">
      <a className="skip" href="#isi">
        {t.skipToContent}
      </a>

      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="wordmark">{t.appName}</span>
        </div>
        <div className="outlet-chip">
          <Icon name="store" size={20} />
          <div>
            <div className="outlet-name">{name || t.loading}</div>
            <div className="outlet-sub">{biz.data?.outlet.name}</div>
          </div>
        </div>
        <nav aria-label={t.menuMain}>
          {groups.map((g) => (
            <div key={g.title} className="nav-group">
              <div className="nav-title">{g.title}</div>
              <Links items={g.items} />
            </div>
          ))}
        </nav>
        <button type="button" className="nav-link sidebar-logout" onClick={() => void logout()}>
          <Icon name="logout" />
          <span>{t.logout}</span>
        </button>
      </aside>

      <header className="topbar">
        <span className="wordmark">{t.appName}</span>
        <span className="biz">{name}</span>
        <AccountMenu key={pathname} name={name} />
      </header>

      <main id="isi" className="main" tabIndex={-1}>
        <div className="page">
          <SubscriptionBanner />
          <Outlet />
          <LegalLinks />
        </div>
      </main>

      <nav className="bottom-nav" aria-label={t.menuBottom}>
        <Links items={bottomItems} />
      </nav>
    </div>
  )
}
